/**
 * Scenario Steps Sidebar component for Create Scenario page
 */
import {
    AttachMoney,
    ChevronLeft,
    ChevronRight,
    CompareArrows,
    Edit as EditIcon,
    ModelTraining,
    Public,
    ShowChart,
    Visibility as VisibilityIcon,
    WaterDrop,
    WaterfallChart,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';

const STEPS_SIDEBAR_WIDTH = 160;
const STEPS_SIDEBAR_COLLAPSED_WIDTH = 64;

export type ScenarioStep = 'general' | 'layers' | 'model' | 'analogs' | 'wells' | 'economy' | 'calculations' | 'results';

interface ScenarioStepsSidebarProps {
  currentStep: ScenarioStep;
  onStepChange: (step: ScenarioStep) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mainSidebarCollapsed?: boolean;
  readOnly?: boolean;
  /** Mobile: full-height temporary drawer */
  variant?: 'permanent' | 'temporary';
  open?: boolean;
  onClose?: () => void;
  scenarioMode?: 'ml' | 'manual';
  /** Show Edit / View switch when user can toggle modes */
  canSwitchMode?: boolean;
  onSwitchMode?: () => void;
  modeSwitchBusy?: boolean;
}

const STEP_ICONS: Record<ScenarioStep, React.ReactNode> = {
  general: <Public fontSize="small" />,
  layers: <WaterDrop fontSize="small" />,
  model: <ModelTraining fontSize="small" />,
  analogs: <CompareArrows fontSize="small" />,
  wells: <WaterfallChart fontSize="small" />,
  economy: <AttachMoney fontSize="small" />,
  calculations: <ShowChart fontSize="small" />,
  results: <AttachMoney fontSize="small" />,
};

const STEPS: { key: ScenarioStep; label: string; description: string }[] = [
  { key: 'general', label: 'General', description: 'Basic information' },
  { key: 'layers', label: 'Reservoirs', description: 'Geological reservoirs' },
  { key: 'model', label: 'Model', description: 'ML model configuration' },
  { key: 'analogs', label: 'Analogues', description: 'Type-well analogues' },
  { key: 'wells', label: 'Reservoir configuration', description: 'Contour, wells & schedule' },
  // { key: 'economy', label: 'Economy', description: 'Economic parameters' }, // Hidden but not removed
  { key: 'calculations', label: 'Production results', description: 'Forecasts & charts' },
  { key: 'results', label: 'Economic results', description: 'NPV & cash flow' },
];

function visibleSteps(readOnly?: boolean, scenarioMode?: 'ml' | 'manual') {
  return STEPS.filter((step) => {
    if (step.key === 'analogs' && scenarioMode !== 'manual') return false;
    if (step.key === 'model' && scenarioMode !== 'ml') return false;
    return true;
  });
}

/** Label for the current step (same rules as sidebar visibility) */
export function getScenarioStepLabel(step: ScenarioStep, readOnly?: boolean, scenarioMode?: 'ml' | 'manual'): string {
  const list = visibleSteps(readOnly, scenarioMode);
  const found = list.find((s) => s.key === step);
  return found?.label ?? STEPS.find((s) => s.key === step)?.label ?? step;
}

export const ScenarioStepsSidebar: React.FC<ScenarioStepsSidebarProps> = ({
  currentStep,
  onStepChange,
  collapsed = false,
  onToggleCollapse,
  mainSidebarCollapsed: _mainSidebarCollapsed = false,
  readOnly = false,
  variant = 'permanent',
  open: temporaryOpen = false,
  onClose,
  scenarioMode = 'ml',
  canSwitchMode = false,
  onSwitchMode,
  modeSwitchBusy = false,
}) => {
  const isTemporary = variant === 'temporary';
  const switchLabel = readOnly ? 'Edit' : 'View';
  const switchTitle = readOnly
    ? 'Switch to edit mode'
    : 'Save and switch to view mode';

  return (
    <Drawer
      variant={isTemporary ? 'temporary' : 'permanent'}
      open={isTemporary ? temporaryOpen : undefined}
      onClose={isTemporary ? onClose : undefined}
      ModalProps={isTemporary ? { keepMounted: true } : undefined}
      sx={{
        width: isTemporary ? undefined : (collapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH),
        maxWidth: isTemporary ? undefined : (collapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH),
        flexShrink: 0,
        transition: 'width 0.3s ease',
        zIndex: isTemporary ? (t) => t.zIndex.drawer + 1 : undefined,
        '& .MuiDrawer-paper': {
          width: collapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
          maxWidth: collapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRight: 1,
          borderColor: 'divider',
          position: isTemporary ? undefined : 'relative',
          height: isTemporary ? '100dvh' : '100%',
          ...(isTemporary && {
            display: 'flex',
            flexDirection: 'column',
          }),
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          overflowY: 'hidden',
        },
      }}
    >
      <Box sx={{
        ...(isTemporary
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : {
              position: 'relative',
              height: 'calc(100vh - 64px)',
              display: 'flex',
              flexDirection: 'column',
              pb: 7,
              overflow: 'hidden',
            }),
      }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: collapsed ? 0 : 1 }}>
            {!collapsed && (
              <Typography variant="h6" color="primary" fontWeight="bold">
                {readOnly ? 'View' : 'Edit'}
              </Typography>
            )}
          </Box>
          {canSwitchMode && onSwitchMode && (
            collapsed && !isTemporary ? (
              <Tooltip title={switchTitle}>
                <span>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={onSwitchMode}
                    disabled={modeSwitchBusy}
                    aria-label={switchTitle}
                    sx={{ display: 'flex', mx: 'auto' }}
                  >
                    {readOnly ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button
                fullWidth
                size="small"
                variant={readOnly ? 'contained' : 'outlined'}
                startIcon={readOnly ? <EditIcon /> : <VisibilityIcon />}
                onClick={onSwitchMode}
                disabled={modeSwitchBusy}
                sx={{ mt: collapsed ? 0 : 0.5, textTransform: 'none' }}
              >
                {modeSwitchBusy ? 'Saving…' : switchLabel}
              </Button>
            )
          )}
        </Box>

        {/* Steps List */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: isTemporary ? 'auto' : 'hidden' }}>
          <List sx={{ pt: 2 }}>
            {visibleSteps(readOnly, scenarioMode).map((step) => (
              <ListItem key={step.key} disablePadding>
                <ListItemButton
                  selected={currentStep === step.key}
                  onClick={() => {
                    onStepChange(step.key);
                    if (isTemporary) {
                      onClose?.();
                    }
                  }}
                  sx={{
                    mx: 0.5,
                    borderRadius: 1,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    overflow: 'hidden',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  }}
                >
                  {collapsed && !isTemporary && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                      }}
                    >
                      {STEP_ICONS[step.key]}
                    </Box>
                  )}
                  {!collapsed && (
                    <ListItemText
                      primary={step.label}
                      secondary={step.description}
                      sx={{
                        m: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        '& .MuiListItemText-primary': {
                          m: 0,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        },
                        '& .MuiListItemText-secondary': {
                          m: 0,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        },
                      }}
                      primaryTypographyProps={{
                        fontSize: '0.8125rem',
                        fontWeight: currentStep === step.key ? 600 : 400,
                        lineHeight: 1.25,
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.6875rem',
                        lineHeight: 1.2,
                        color: currentStep === step.key ? 'primary.contrastText' : 'text.secondary',
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Collapse button at bottom (desktop only) */}
        {!isTemporary && onToggleCollapse && (
          <Box sx={{
            position: 'absolute',
            bottom: 8,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            '& .MuiIconButton-root': {
              pointerEvents: 'auto',
            },
          }}>
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              sx={{
                color: 'primary.main',
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.dark',
                },
              }}
              title={collapsed ? "Expand steps sidebar" : "Collapse steps sidebar"}
            >
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export { STEPS_SIDEBAR_COLLAPSED_WIDTH, STEPS_SIDEBAR_WIDTH };
